import { useCallback } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, IconButton, Typography } from '@altermaind/web/ui';
// eslint-disable-next-line @altermaind/no-build-import
import hardSkillAnswerGuide from '@/_shared/assets/documents/Hard_skill_assessment_Spiegazione_risposte.pdf';
import { Charts } from '@/_shared/components/Charts';
import { ReadMoreTypography } from '@/_shared/components/ReadMoreTypography';
import { downloadFile } from '@/_shared/helpers/download';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  getTemplatesCall,
  useCurrentTemplate,
} from '../../state';

export const RecapOrganizationResults = () => {
  const t = useTranslations('surveys.details.stats.tabOrganization');
  const { genericResponsiveValue } = useResponsiveStyle();

  const { organizationStats, proElementsOrganizationStats, conElementsOrganizationStats } =
    getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const { currentName, currentTemplateId, isHardSkillSurvey } = useCurrentTemplate();

  const getDescriptionMetric = useCallback(
    (surveyTemplateId: string, metricName: string) => {
      if (!responseGetTemplates) {
        return '';
      }
      return (
        responseGetTemplates
          ?.find(template => template.id === surveyTemplateId)
          ?.metrics?.find(metric => metric.name === metricName)?.description ?? ''
      );
    },
    [responseGetTemplates],
  );

  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb='xs'
        flexDirection={genericResponsiveValue({
          breakPoints: { mobile: 'column' },
          defaultValue: 'row',
        })}
      >
        <Typography variant='h2'>
          {t('title', { currentName: currentName.toLowerCase() })}
        </Typography>
        {isHardSkillSurvey ? (
          <IconButton
            prefixSvg='download'
            svgStyle={{ fill: 'primary/text', size: 'xs' }}
            textStyle={{ ml: 'xxxs' }}
            onClick={() =>
              downloadFile(hardSkillAnswerGuide, 'Hard Skill assessment Spiegazione risposte')
            }
            variant='link/primary'
          >
            {t('downloadAnswerGuide')}
          </IconButton>
        ) : null}
      </Box>
      <Box
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        mb='l'
      >
        <Box flex='2'>
          <Typography variant='p3/light' mb='l'>
            {t('description', {
              currentName: currentName.toLowerCase(),
              metricsCount: organizationStats?.metricsScore?.length ?? 0,
            })}
          </Typography>
          <Charts.RadarChartComponent
            data={
              Array.isArray(organizationStats?.metricsScore)
                ? organizationStats?.metricsScore.map(item => ({
                    subject: item.metric?.name ?? '',
                    current: item.score ?? 0,
                    expected: 7,
                    description: getDescriptionMetric(
                      currentTemplateId || '',
                      item.metric?.name || '',
                    ),
                  }))
                : []
            }
          />
        </Box>
        <Box
          flex='1'
          mt={genericResponsiveValue({
            breakPoints: { desktop: 'l' },
            defaultValue: 'm',
          })}
        >
          <Card>
            {organizationStats?.insight && (
              <>
                <Typography variant='h4'>{t('insignBox.title')}</Typography>
                <Box display='flex' flexDirection='column' mb='m' mt='xs'>
                  <ReadMoreTypography variant='p3/light'>
                    {organizationStats?.insight}
                  </ReadMoreTypography>
                </Box>
              </>
            )}
            <Typography variant='h4'>{t('insignBox.proTitle')}</Typography>
            <Box display='flex' flexDirection='column' mb='m' mt='xs'>
              {proElementsOrganizationStats?.map(item => (
                <Card
                  mb='xxs'
                  backgroundColor='primary/inactive'
                  key={item.metric?.name}
                  px='xs'
                  py='xxxs'
                  width='fit-content'
                >
                  <Typography variant='p2/light' fontSize='xs'>
                    {item.metric?.name}
                  </Typography>
                </Card>
              ))}
            </Box>
            <Typography variant='h4'>{t('insignBox.conTitle')}</Typography>
            <Box display='flex' flexDirection='column' mt='xs'>
              {conElementsOrganizationStats?.map(item => (
                <Card
                  mb='xxs'
                  backgroundColor='error/opacity'
                  key={item.metric?.name}
                  px='xs'
                  py='xxxs'
                  width='fit-content'
                >
                  <Typography variant='p2/light' fontSize='xs'>
                    {item.metric?.name}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
};
